// Copyright (c) Ping. All rights reserved.

import { useMemo } from 'react';

import { TabView, type TabViewRoute } from '@ping/ui';

import { ActivityRouteHost } from './hosts/ActivityRouteHost';
import { BusinessDirectoryRouteHost } from './hosts/BusinessDirectoryRouteHost';
import { CallsRouteHost } from './hosts/CallsRouteHost';
import { ChatRouteHost } from './hosts/ChatRouteHost';
import { ContactsRouteHost } from './hosts/ContactsRouteHost';
import { LeaderboardRouteHost } from './hosts/LeaderboardRouteHost';
import { NotesRouteHost } from './hosts/NotesRouteHost';
import { TasksRouteHost } from './hosts/TasksRouteHost';
import { useRouter, type WebRoute } from './RouterContext';

/**
 * Right-pane router, driven through the shared <TabView>. Each destination is
 * a TabView scene; the sidebar (via the router) sets the active index. Scenes
 * are `lazy` — a destination mounts the first time it's visited and then STAYS
 * mounted, so re-visiting is instant and animated with no re-fetch / re-mount
 * (chat keeps its scroll + subscriptions alive in the background, etc.).
 *
 * Modals (`/settings`, `/status`) aren't routes here — they live in the
 * `AppShell` overlay layer and fall back to the Chat scene behind the modal.
 */
const SCENES: ReadonlyArray<{
  readonly key: string;
  readonly route: WebRoute;
  readonly render: () => JSX.Element;
}> = [
  { key: 'chat', route: '/chat', render: () => <ChatRouteHost /> },
  { key: 'activity', route: '/activity', render: () => <ActivityRouteHost /> },
  { key: 'contacts', route: '/contacts', render: () => <ContactsRouteHost /> },
  { key: 'calls', route: '/calls', render: () => <CallsRouteHost /> },
  { key: 'tasks', route: '/tasks', render: () => <TasksRouteHost /> },
  { key: 'notes', route: '/notes', render: () => <NotesRouteHost /> },
  { key: 'leaderboard', route: '/leaderboard', render: () => <LeaderboardRouteHost /> },
  { key: 'directory', route: '/business-directory', render: () => <BusinessDirectoryRouteHost /> },
];

const ROUTES: readonly TabViewRoute[] = SCENES.map((s) => ({ key: s.key }));

// Route → scene index. `/settings` + `/status` (overlay-owned) and any unknown
// route fall back to the Chat scene so the body always shows something.
const ROUTE_TO_INDEX: Partial<Record<WebRoute, number>> = Object.fromEntries(
  SCENES.map((s, i) => [s.route, i]),
);

export function MainContent(): JSX.Element {
  const router = useRouter();
  const index = useMemo(() => ROUTE_TO_INDEX[router.route] ?? 0, [router.route]);

  return (
    <div data-testid="shell-main" className="flex flex-row h-full min-h-0 w-full">
      <TabView
        routes={ROUTES}
        index={index}
        onIndexChange={(i) => {
          const next = SCENES[i];
          if (next) router.push(next.route);
        }}
        // The sidebar is the control surface — no built-in tab bar, and no
        // swipe (full-page sections shouldn't pan under a horizontal drag).
        renderTabBar={() => null}
        swipeEnabled={false}
        // Instant swap (no horizontal sweep) — scenes still mount lazily and
        // stay mounted, so switching is immediate with no re-fetch.
        animationEnabled={false}
        lazy
        renderScene={({ route }) =>
          SCENES.find((s) => s.key === route.key)?.render() ?? <ChatRouteHost />
        }
      />
    </div>
  );
}
